import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  MapPin, 
  Clock, 
  DollarSign, 
  Building2, 
  ExternalLink,
  Star,
  Users,
  Calendar
} from 'lucide-react';
import useBehaviorTracking from '../../hooks/useBehaviorTracking';
const JobCard = ({ job, onApply, onViewDetails }) => {
  const { trackJobView } = useBehaviorTracking();
  useEffect(() => {
    trackJobView(job.id,{title: job.title, company: job.company});
  }, [job.id]);

  const {
    title = 'Job Title',
    company_name = 'Company Name',
    location = 'Location',
    job_type = 'full_time',
    salary_min = 0,
    salary_max = 0,
    currency = 'INR',
    posted_date = '',
    similarity_score = 0,
    overall_score = 0,
    skills_required = [],
    ai_insight = '',
    recommendation_rank = 0,
    fit_explanation = '',
    external_url = '',
    job_id
  } = job;

  const getJobTypeColor = (type) => {
    switch (type) {
      case 'full_time':
        return 'bg-green-100 text-green-800';
      case 'part_time':
        return 'bg-blue-100 text-blue-800';
      case 'contract':
        return 'bg-yellow-100 text-yellow-800';
      case 'internship':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getScoreColor = (score) => {
    if (score >= 0.8) return 'text-green-600';
    if (score >= 0.6) return 'text-yellow-600';
    return 'text-red-600';
  };

  const formatSalary = (min, max, currency) => {
    if (!min && !max) return 'Salary not specified';
    const formatNumber = (num) => {
      if (num >= 100000) return `${(num / 100000).toFixed(1)}L`;
      if (num >= 1000) return `${(num / 1000).toFixed(0)}K`;
      return num.toString();
    };
    return `${currency} ${formatNumber(min)} - ${formatNumber(max)}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) return '1 day ago';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.ceil(diffDays / 7)} weeks ago`;
    return `${Math.ceil(diffDays / 30)} months ago`;
  };

  return (
    <motion.div
      whileHover={{ y: -4 }}
      className="card hover-lift group cursor-pointer"
      onClick={() => onViewDetails?.(job)}
    >
      <div className="card-content">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 group-hover:text-primary-600 transition-colors">
              {title}
            </h3>
            <div className="flex items-center space-x-2 mt-1">
              <Building2 className="h-4 w-4 text-gray-400" />
              <span className="text-sm text-gray-600">{company_name}</span>
            </div>
          </div>
          {overall_score > 0 && (
            <div className="text-right">
              <div className={`text-sm font-medium ${getScoreColor(overall_score)}`}>
                {Math.round(overall_score * 100)}% match
              </div>
              {recommendation_rank > 0 && (
                <div className="text-xs text-gray-500">#{recommendation_rank} recommendation</div>
              )}
            </div>
          )}
        </div>

        {/* AI Insight */}
        {ai_insight && (
          <div className="bg-primary-50 border border-primary-200 rounded-lg p-3 mb-4">
            <div className="flex items-start space-x-2">
              <div className="w-2 h-2 bg-primary-500 rounded-full mt-2 flex-shrink-0" />
              <div>
                <p className="text-xs font-medium text-primary-800">AI Analysis</p>
                <p className="text-xs text-primary-700 mt-1">{ai_insight}</p>
              </div>
            </div>
          </div>
        )}

        {/* Job Details */}
        <div className="space-y-3 mb-4">
          <div className="flex items-center space-x-4 text-sm text-gray-600">
            <div className="flex items-center space-x-1">
              <MapPin className="h-4 w-4" />
              <span>{location}</span>
            </div>
            <div className="flex items-center space-x-1">
              <Clock className="h-4 w-4" />
              <span className="capitalize">{job_type.replace('_', ' ')}</span>
            </div>
            {posted_date && (
              <div className="flex items-center space-x-1">
                <Calendar className="h-4 w-4" />
                <span>{formatDate(posted_date)}</span>
              </div>
            )}
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-1 text-sm text-gray-600">
              <DollarSign className="h-4 w-4" />
              <span>{formatSalary(salary_min, salary_max, currency)}</span>
            </div>
            <span className={`badge ${getJobTypeColor(job_type)}`}>
              {job_type.replace('_', ' ')}
            </span>
          </div>
        </div>

        {/* Skills Required */}
        {skills_required.length > 0 && (
          <div className="mb-4">
            <p className="text-xs font-medium text-gray-700 mb-2">Required skills:</p>
            <div className="flex flex-wrap gap-1">
              {skills_required.slice(0, 4).map((skill, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                >
                  {skill}
                </span>
              ))}
              {skills_required.length > 4 && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                  +{skills_required.length - 4} more
                </span>
              )}
            </div>
          </div>
        )}

        {/* Fit Explanation */}
        {fit_explanation && (
          <div className="mb-4 p-2 bg-gray-50 rounded text-xs text-gray-600">
            {fit_explanation}
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-200">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onApply?.(job);
            }}
            className="btn btn-primary btn-sm"
          >
            Apply Now
          </button>
          <div className="flex items-center space-x-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onViewDetails?.(job);
              }}
              className="btn btn-ghost btn-sm"
            >
              View Details
            </button>
            {external_url && (
              <a
                href={external_url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="btn btn-ghost btn-sm"
              >
                <ExternalLink className="h-4 w-4" />
              </a>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default JobCard;
